import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Form, Modal, Alert, Card, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { departmentService } from '../../services/api';

const DepartmentList = () => {
  const { isAdmin } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await departmentService.getAllDepartments();
      if (response.data && response.data.success) {
        setDepartments(response.data.data || []);
        setError('');
      } else {
        setError('Failed to load departments data');
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError('Failed to load departments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddDepartment = async () => {
    if (!formData.name) {
      return setError('Department name is required');
    }
    
    try {
      setError('');
      await departmentService.createDepartment(formData);
      
      // Reset form and close modal
      setFormData({ name: '', description: '' });
      setShowAddModal(false);
      
      // Show success message and refresh departments
      setSuccess('Department created successfully!');
      fetchDepartments();
      
      // Clear success message after a delay
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating department:', err);
      setError('Failed to create department. Please try again.');
    }
  };

  const handleEditClick = (department) => {
    setCurrentDepartment(department);
    setFormData({
      name: department.name,
      description: department.description || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateDepartment = async () => {
    if (!formData.name) {
      return setError('Department name is required');
    }
    
    try {
      setError('');
      await departmentService.updateDepartment(currentDepartment.id, formData);
      
      // Reset form and close modal
      setFormData({ name: '', description: '' });
      setShowEditModal(false);
      setCurrentDepartment(null);
      
      // Show success message and refresh departments
      setSuccess('Department updated successfully!');
      fetchDepartments();
      
      // Clear success message after a delay
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating department:', err);
      setError('Failed to update department. Please try again.');
    }
  };

  const handleDeleteDepartment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) {
      return;
    }
    
    try {
      setError('');
      await departmentService.deleteDepartment(id);
      
      // Show success message and refresh departments
      setSuccess('Department deleted successfully!');
      fetchDepartments();
      
      // Clear success message after a delay
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting department:', err);
      setError('Failed to delete department. Please try again.');
    }
  };

  if (loading && departments.length === 0) {
    return <div className="text-center p-5">Loading departments...</div>;
  }

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Departments</h2>
        {isAdmin && (
          <Button 
            variant="primary" 
            onClick={() => setShowAddModal(true)}
          >
            Add Department
          </Button>
        )}
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      {departments.length === 0 ? (
        <Alert variant="info">
          No departments found. {isAdmin && 'Click "Add Department" to create one.'}
        </Alert>
      ) : (
        <Row>
          {departments.map(department => (
            <Col md={4} key={department.id} className="mb-4">
              <Card>
                <Card.Body>
                  <Card.Title>{department.name}</Card.Title>
                  <Card.Text>
                    {department.description || 'No description available.'}
                  </Card.Text>
                  {isAdmin && (
                    <div className="d-flex justify-content-end">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        className="me-2"
                        onClick={() => handleEditClick(department)}
                      >
                        Edit
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteDepartment(department.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      
      {/* Add Department Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Department</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddDepartment}>
            Add Department
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Edit Department Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Department</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateDepartment}>
            Update Department
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DepartmentList;
